// Every name here is an `event` value in the ClickHouse `events` table that
// Jitsu writes on anal-01, and Metabase reads through the "analytics"
// connection. Property keys are flattened to snake_case columns there:
// `{ eventId }` lands in `event_id`, `{ planId }` in `plan_id`.
//
// Two rules keep the data queryable:
// - Prefix by domain (`eventAction*`, `paywallAction*`, ...), so one LIKE
//   catches a whole funnel.
// - Record failures and both branches of a choice, not only the happy path
//   -- a funnel that only counts successes reports 100%.

export enum CommonAnalytics {
  APP_OPEN = 'appOpen',
  /**
   * A deploy replaced the chunks the open session was pointing at, and the app
   * reloaded itself to pick up the new build. `fromVersion` is the build that
   * broke, `toVersion` the one that answered, and `recovered` says whether the
   * reload actually got a different build -- the failed branch is the one that
   * says this recovery does not work, so both are sent. See
   * `shared/lib/staleChunk.ts`.
   */
  STALE_CHUNK_RELOAD = 'appStaleChunkReload',
}

export enum EventsAnalytics {
  ADD_TO_GOOGLE = 'eventActionAddToGoogle',
  CHANGE_NOTIFICATION = 'eventActionChangeNotification',
  COPY_LINK = 'eventActionCopyLink',
  /**
   * The API answered: `ok` plus `status` on failure, and `recurrentType` on
   * both branches so a recurrence funnel can be read from this event alone
   * rather than by joining it back to CREATE_SUBMIT per session.
   */
  CREATE_RESULT = 'eventActionCreateResult',
  /**
   * The form was opened. `source` says from where, `titleHint` which example
   * title was the placeholder (see `pickTitleHint`).
   */
  CREATE_START = 'eventActionCreateStart',
  /**
   * The Create button was pressed. Carries which form features were used,
   * `titleHint` as above, and `titleBlank` when the title was left empty and
   * the "Untitled" fallback went to the API.
   */
  CREATE_SUBMIT = 'eventActionCreateSubmit',
  DELETE = 'eventActionDelete',
  /** What the user chose in the delete dialog: single, all, cancel. */
  DELETE_CONFIRM = 'eventActionDeleteConfirm',
  DELETE_RESULT = 'eventActionDeleteResult',
  EDIT = 'eventActionEdit',
  EDIT_RESULT = 'eventActionEditResult',
  EDIT_SUBMIT = 'eventActionEditSubmit',
  /** Zod rejected the form; `field` names the first failing one. */
  FORM_ERROR = 'eventActionFormError',
  /**
   * How a join-link open ended. `outcome` is one of:
   * `success`, `alreadySet`, `skippedOwner`, `skippedSlotBooker`,
   * `eventGone`, `failedAfterRetries`.
   */
  INSTANT_JOIN_RESULT = 'eventActionInstantJoinResult',
  /** A join-link open reached the page and is about to act. */
  INSTANT_JOIN_START = 'eventActionInstantJoinStart',
  /**
   * The RSVP button on the event view page, and **only** that. A join made by
   * following a `joinEvent` / `maybeJoinEvent` link has never fired this --
   * that path sent no events at all until 2026-09-11 -- so any funnel built on
   * it before then measured the manual path alone.
   */
  JOIN = 'eventActionJoin',
  MAYBE = 'eventActionMaybe',
  NOT_INTERESTED = 'eventActionNotInterested',
  /** continue | cancel on the overlap sheet. */
  OVERLAP_CHOICE = 'eventActionOverlapChoice',
  /** The overlap sheet actually appeared, with `count` overlapping events. */
  OVERLAP_SHOWN = 'eventActionOverlapShown',
  /**
   * The "only this one / all events" dialog was dismissed, so nothing was
   * joined. `source` says which path raised it.
   */
  RECURRENCE_DECLINED = 'eventActionRecurrenceDeclined',
  SHARE = 'eventActionShare',
  /** Outcome of the share: `method` shareMessage | shareDialog, `sent` when Telegram reports it. */
  SHARE_RESULT = 'eventActionShareResult',
  /** The story picker opened: `eventId`, `fromFeed`. */
  STORY_SHARE_OPEN = 'eventActionStoryShareOpen',
  /**
   * What became of a Continue: `ok`, and on failure `stage` -- `render` when
   * POST /stories failed or saved nothing, `share` when Telegram refused
   * shareToStory (clients older than Bot API 7.8). `ok` means Telegram's story
   * editor was handed the image; Telegram does not say whether it was posted.
   */
  STORY_SHARE_RESULT = 'eventActionStoryShareResult',
  /** A background tapped: `backgroundId`, and `position` in this visit's shuffled list. */
  STORY_SHARE_SELECT = 'eventActionStoryShareSelect',
  /**
   * Continue pressed: `backgroundId`, `position`, `widgetLink`, and
   * `previewReady` (whether the event layer had loaded). Before the picker
   * change that added STORY_SHARE_RESULT, this fired once the story was already
   * rendered and meant "handed to Telegram"; it now fires before the render.
   */
  STORY_SHARE_SUBMIT = 'eventActionStoryShareSubmit',
  /**
   * A creation from the New Event form was assigned to an arm of the
   * pre-selected theme experiment, control included -- one per event, while
   * the experiment runs (`themePreselectExperiment.ts`). `eventId`, `arm`
   * (`control` or `treated`), `preset` (the theme the treated arm gets; for
   * control, the one it would have got) and `source` (the form's entry point,
   * as on CREATE_RESULT). Not sent for events made from a draft.
   */
  THEME_PRESELECT = 'eventActionThemePreselect',
  /**
   * A theme was saved: fired once the PATCH succeeded, never for a pick that
   * was cancelled, superseded or failed. `preset` is the new theme and
   * `previousPreset` the one it replaced, each a preset id or `plain`.
   * `source`: `event_page` (the panel's Set theme), `success_screen` (a
   * swatch on the creation success screen, saved ~600ms after the last tap)
   * or `auto_experiment` (the pre-selected theme experiment, which set it for
   * the owner: not an owner's choice).
   */
  THEME_SET = 'eventActionThemeSet',
  /** The same save failed and the theme reverted: THEME_SET's properties. */
  THEME_SET_FAILED = 'eventActionThemeSetFailed',
  /**
   * The one-time "New: give your event a theme" tooltip was shown on the
   * creation success screen: `eventId`. At most once per user (the seen flag
   * is in Telegram CloudStorage, so it follows the account across devices).
   */
  THEME_TOOLTIP_SHOWN = 'eventActionThemeTooltipShown',
}

export enum StacksAnalytics {
  /**
   * The deep-link wait ended with no link: the group picker was opened and
   * backed out of, or the journey ended somewhere this app cannot see.
   *
   * Deliberately not a failure -- `CHAT_CONNECT_FAILED` stays for the request
   * that errored, and nothing here knows whether the user changed their mind
   * or the backend was slow. It exists because the alternative is worse: an
   * attempt that produces neither this nor `CHAT_CONNECTED` is unmeasurable,
   * and 47 people attempting against 18 connecting says nothing on its own.
   *
   * Undercounts exactly as `CHAT_CONNECTED` does: both need the mini-app that
   * started the journey to still be mounted when it ends, and a relaunch
   * between the picker and the return loses that.
   */
  CHAT_CONNECT_ABANDONED = 'stackActionChatConnectAbandoned',
  CHAT_CONNECT_ATTEMPT = 'stackActionChatConnectAttempt',
  CHAT_CONNECT_FAILED = 'stackActionChatConnectFailed',
  CHAT_CONNECTED = 'stackActionChatConnected',
  CHAT_DISCONNECTED = 'stackActionChatDisconnected',
  COPY_LINK = 'stackActionCopyLink',
  SHARE_LINK = 'stackActionShareLink',
  SHARE_RESULT = 'stackActionShareResult',
  SUBSCRIBE = 'stackActionSubscribe',
  UNSUBSCRIBE = 'stackActionUnsubscribe',
}

export enum SlotsAnalytics {
  ATTEND_PAGE_CONTINUE = 'slotsActionAttendPageContinue',
  COPY_LINK = 'slotsActionCopyLink',
  DAY_CLICK = 'slotsActionDayClick',
  EVENTS_TAB = 'slotsActionEventsTab',
  LINK_DISABLE = 'slotsActionLinkDisable',
  LINK_ENABLE = 'slotsActionLinkEnable',
  NOTIFICATION_CHANGE = 'slotsActionNotificationChange',
  ONBOARDING_SUBMIT = 'slotsActionOnboardingSubmit',
  RESCHEDULE_BUTTON = 'slotsActionRescheduleButton',
  RESCHEDULE_SUBMIT = 'slotsActionRescheduleSubmit',
  SETTINGS_SUBMIT = 'slotsActionSettingsSubmit',
  SHARE_CLICK = 'slotsActionShareClick',
  SHARE_RESULT = 'slotsActionShareResult',
  SLOTS_MAIN_TABS_CLICK = 'slotsActionSlotsMainTabsClick',
  SLOTS_TAB = 'slotsActionSlotsTab',
  START_PAGE_CONTINUE_CLICK = 'slotsActionStartPageContinueClick',
  TIME_CLICK = 'slotsActionTimeClick',
  TIME_SUBMIT = 'slotsActionTimeSubmit',
}

/** Where the purchase sheet was opened from. One value per entry point. */
export type PaywallSource =
  | 'deeplink'
  | 'eventNotifications'
  | 'feedBanner'
  | 'googleAccountDisabled'
  | 'settingsBanner'
  | 'settingsExternalCalendars'
  | 'settingsNotifications'
  | 'settingsResume'
  | 'slotsAvailability';

export enum PaywallAnalytics {
  CANCEL_RESULT = 'paywallActionCancelResult',
  CANCEL_START = 'paywallActionCancelStart',
  CANCEL_SUBMIT = 'paywallActionCancelSubmit',
  /** The sheet closed; `subscribed` says whether the user now has Pro. */
  CLOSE = 'paywallActionClose',
  /** Telegram's invoice sheet closed: paid | cancelled | failed | pending. */
  INVOICE_RESULT = 'paywallActionInvoiceResult',
  /** The purchase sheet became visible. */
  OPEN = 'paywallActionOpen',
  /** The user switched plan (monthly / yearly). */
  PLAN_SELECT = 'paywallActionPlanSelect',
  /** The profile poll confirmed an active subscription. */
  PURCHASE_CONFIRMED = 'paywallActionPurchaseConfirmed',
  /** The Subscribe button was pressed; the invoice is being created. */
  SUBSCRIBE_CLICK = 'paywallActionSubscribeClick',
}

export enum OnboardingAnalytics {
  /** An example card on the "create your first event" screen. */
  EXAMPLE_PICK = 'onboardingActionExamplePick',
  GOOGLE_CONNECT = 'onboardingActionGoogleConnect',
  GOOGLE_SKIP = 'onboardingActionGoogleSkip',
  /**
   * An onboarding surface was actually rendered; `screen` says which. This is
   * the denominator every other event in this group lacked -- without it a
   * choice event that never arrives is indistinguishable from a screen nobody
   * reached. Fires once per mount, and only once the surface is past its
   * loading and redirect conditions.
   */
  SCREEN_SHOWN = 'onboardingActionScreenShown',
  /** event | slots | later on a welcome screen; `screen` says which one. */
  WELCOME_CHOICE = 'onboardingActionWelcomeChoice',
}

/**
 * The onboarding surfaces, as the `screen` property reports them.
 *
 * `welcomeEvent` and `joinUpsell` are the same widget in two places -- the
 * standalone "create your first event" page, and the offer on the confirmation
 * screen after joining an invite. They shared the name `welcomeEvent` until
 * 2026-09-11, so rows before that date cannot be told apart.
 */
export type OnboardingScreen =
  | 'eventDeclined'
  | 'google'
  | 'joinUpsell'
  | 'welcome'
  | 'welcomeEvent';

export type GoogleConnectSource =
  | 'attachLinks'
  | 'onboarding'
  | 'settingsExternalCalendars'
  | 'slotsGoogleInit'
  | 'slotsVideoCall'
  | 'videoCallSettings';

export enum GoogleAnalytics {
  ACCOUNT_DELETE = 'googleActionAccountDelete',
  ACCOUNT_MAKE_DEFAULT = 'googleActionAccountMakeDefault',
  ACCOUNT_RESYNC = 'googleActionAccountResync',
  /**
   * Outcome on the external /google-auth page. `missingScopes` rides along on
   * `permissions-failed` only, listing what was asked for and not granted. That tab runs outside Telegram,
   * so nothing identifies the user there on its own -- the mini-app hands the
   * id over in `?uid=` and the page calls identify() before this event, which
   * is what makes it joinable. Rows written before 2026-09-10 have no user_id.
   */
  AUTH_RESULT = 'googleActionAuthResult',
  CALENDARS_SYNC_SUBMIT = 'googleActionCalendarsSyncSubmit',
  /** The Google auth window was requested. */
  CONNECT_START = 'googleActionConnectStart',
  /** The profile poll saw the account appear. */
  CONNECT_SUCCESS = 'googleActionConnectSuccess',
  /** Five minutes of polling without an account. */
  CONNECT_TIMEOUT = 'googleActionConnectTimeout',
  /** The one-time token could not be created; the window never opened. */
  CONNECT_TOKEN_FAILED = 'googleActionConnectTokenFailed',
}

export enum FeedAnalytics {
  /** button (floating) | emptyDay (the "no events" row). */
  CREATE_CLICK = 'feedActionCreateClick',
  /** A day tapped in the month picker; `daysFromToday` may be negative. */
  DAY_SELECT = 'feedActionDaySelect',
  EVENT_OPEN = 'feedActionEventOpen',
  MONTH_CHANGE = 'feedActionMonthChange',
  SETTINGS_CLICK = 'feedActionSettingsClick',
  TODAY_CLICK = 'feedActionTodayClick',
}

export enum VideoCallAnalytics {
  /** `provider` meet | zoom, `enabled` the new state. */
  TOGGLE = 'videoCallActionToggle',
  ZOOM_CONNECT_CLICK = 'videoCallActionZoomConnectClick',
  ZOOM_CONNECTED = 'videoCallActionZoomConnected',
  ZOOM_DISCONNECT = 'videoCallActionZoomDisconnect',
}

/** Where the birthdays page was opened from, as `birthdaysActionPageShown` reports it. */
export type BirthdaysSource =
  | 'deeplink'
  | 'feedItem'
  | 'feedStrip'
  | 'settings';

export enum BirthdaysAnalytics {
  /** "Add to a group chat" on the birthdays page; opens Telegram's group picker. */
  ADD_TO_GROUP_CLICK = 'birthdaysActionAddToGroupClick',
  /**
   * A group's "show in my calendar" switch; `on` says which way, and `source`
   * where: the birthdays page's switch, or "Don't show birthdays from …" on a
   * birthday event (`event_page`, always off).
   */
  CALENDAR_TOGGLE = 'birthdaysActionCalendarToggle',
  /**
   * A day's collapsed birthdays ("Anna, Dmitry +1") opened in the feed, or
   * closed again; `count` is how many there are that day and `inCalendar` how
   * many of them are calendar events.
   */
  FEED_DAY_COLLAPSE = 'birthdaysActionFeedDayCollapse',
  FEED_DAY_EXPAND = 'birthdaysActionFeedDayExpand',
  /**
   * A birthday row in the feed that is not in the calendar was tapped; it
   * opens the birthdays page. (A calendar one opens its event instead, as
   * feedActionEventOpen with isBirthday.)
   */
  FEED_ITEM_CLICK = 'birthdaysActionFeedItemClick',
  /**
   * The page rendered with its data; `source` says how the user got there and
   * `count` how many birthdays they can see.
   */
  PAGE_SHOWN = 'birthdaysActionPageShown',
}
